import type { ReactNode } from 'react';
import { cn } from '#client/lib/cn';

/**
 * The semantic tone set, matching the status roles in theme/semantic.css:
 *
 *   draft     not started / inactive      severe    needs attention today
 *   info      in progress                 critical  error, overdue, abnormal
 *   success   done, on target             amended   corrected or an outlier
 *   warning   approaching a threshold
 *
 * `neutral` is kept as an alias for `draft` because several screens already
 * use it for plain counts.
 */
export type BadgeTone =
  'neutral' | 'draft' | 'info' | 'success' | 'warning' | 'severe' | 'critical' | 'amended';

interface BadgeProps {
  tone?: BadgeTone | undefined;
  /** A 12px glyph rendered before the label. */
  icon?: ReactNode | undefined;
  children: ReactNode;
  className?: string | undefined;
}

/**
 * The dumb visual primitive.
 *
 * Anything conveying a *status* should go through `StatusBadge`, which requires
 * an icon and a text label as well as a tone — colour alone is not a sufficient
 * signal.
 */
export function Badge({ tone = 'neutral', icon, children, className }: BadgeProps) {
  return (
    <span className={cn('c-badge', `c-badge--${tone}`, className)}>
      {icon && <span aria-hidden="true">{icon}</span>}
      {children}
    </span>
  );
}
